import { ErrorView } from "@/components/ui/layout/error/errorView"
import { useBoxPlanningActions } from "@/modules/boxPlanning/store/useBoxPlanningStore"
import { BoxSlotFormCreate } from "@/modules/boxSlot/components/boxSlotFormCreate"
import { BoxSlotInfoWrapper } from "@/modules/boxSlot/components/boxSlotInfoWrapper"
import { BoxSlotSessionContentDetails } from "@/modules/boxSlotSession/components/boxSlotSessionContentDetails"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useBoxSlotSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const { removeBoxSlotViewed } = useBoxPlanningActions()
  const { clearBoxSlotsBeingCreated } = useBoxPlanningActions()
  const canAddSlot = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)

  const navigateToBoxSlotView = async (boxSlotId: string, destinationView?: "session") => {
    await sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotInfoWrapper boxSlotId={boxSlotId} destinationView={destinationView} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
      onHide: () => {
        void clearBoxSlotsBeingCreated()
        void removeBoxSlotViewed()
        return Promise.resolve(true)
      },
    })
  }

  const navigateToBoxSlotCreation = (boxProgramId: string, startDate: Date, args?: SidebarNavigatorParams) => {
    // PERMISSIONS - Head-coach && Coach & Financial permissions denied
    if (!canAddSlot) {
      return null
    }

    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotFormCreate boxProgramId={boxProgramId} startDate={startDate} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        void clearBoxSlotsBeingCreated()
        return Promise.resolve(true)
      },
      ...args,
    })
  }
  const navigateToBoxSessionView = (sessionId: string, boxSlotId: string) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxSlotSessionContentDetails boxSessionId={sessionId} className="px-5 pt-18" boxSlotId={boxSlotId} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })
  }

  return { navigateToBoxSlotView, navigateToBoxSlotCreation, navigateToBoxSessionView }
}
